/**
 * Mobile design tokens — palette + heat-ramp helper.
 *
 * Used by the new mobile-only chrome (bottom nav, contextual filter bar,
 * MobileHeatmapGrid, sheets). Desktop styling continues to use the existing
 * theme.
 */

export const mobileColors = {
  pageBg:           '#0B0F1E',
  cardSurface:      '#141823',
  heatmapBg:        '#0E1424',
  navBg:            '#0F1530',
  borderSubtle:     'rgba(255,255,255,0.08)',
  borderHair:       'rgba(255,255,255,0.06)',
  rowDivider:       'rgba(255,255,255,0.08)',
  textPrimary:      '#FFFFFF',
  textSecondary:    'rgba(255,255,255,0.65)',
  textTertiary:     'rgba(255,255,255,0.5)',
  accentBlue:       '#5DA1FF',
  accentBlueBg:     'rgba(45,127,249,0.18)',
  accentBlueBorder: 'rgba(45,127,249,0.4)',
  primaryCta:       '#2D7FF9',
  successGreen:     '#34D399',
  successBg:        'rgba(16,185,129,0.16)',
  successBorder:    'rgba(16,185,129,0.35)',
};

/**
 * Heat ramp bands (ascending). Each band applies up to and including its `max`.
 * The final band catches anything above the highest threshold.
 */
export const heatRamp = [
  { max: -50,       bg: '#791F1F', fg: '#FFFFFF' },
  { max: -10,       bg: '#B33D3D', fg: '#FFFFFF' },
  { max:  10,       bg: '#D3D1C7', fg: '#1A1A1A' },
  { max:  50,       bg: '#5DCAA5', fg: '#08291F' },
  { max: 200,       bg: '#0F6E56', fg: '#FFFFFF' },
  { max: Infinity,  bg: '#085041', fg: '#FFFFFF' },
];

/**
 * Map a percent-gain value to a heat-cell color pair.
 * @param {number} p Premium-gain % (or a comparable scalar)
 * @returns {{ bg: string, fg: string }}
 */
export function colorFor(p) {
  if (p == null || isNaN(p)) return { bg: '#1f2937', fg: 'rgba(255,255,255,0.6)' };
  if (p <= -50) return { bg: '#791F1F', fg: '#FFFFFF' };
  if (p <= -10) return { bg: '#B33D3D', fg: '#FFFFFF' };
  if (p < 10)   return { bg: '#D3D1C7', fg: '#1A1A1A' };
  if (p < 50)   return { bg: '#5DCAA5', fg: '#08291F' };
  if (p < 200)  return { bg: '#0F6E56', fg: '#FFFFFF' };
  return            { bg: '#085041', fg: '#FFFFFF' };
}

/**
 * Format a percent-gain value for display in a heatmap cell.
 *   < ±1000% → `+49.8%` / `-95.2%` (1 decimal, signed)
 *   ≥ ±1000% → `+1,323%` / `-2,890%` (integer, comma-separated, signed)
 * Always includes sign. NaN/null → `--`.
 */
export function formatHeatPct(v) {
  if (v == null || isNaN(v)) return '--';
  const n = Number(v);
  const abs = Math.abs(n);
  if (abs < 1000) {
    return `${n >= 0 ? '+' : ''}${n.toFixed(1)}%`;
  }
  const rounded = Math.round(n);
  return `${rounded >= 0 ? '+' : ''}${rounded.toLocaleString('en-US')}%`;
}

/** Format a column-header percent label, e.g. `-20%`, `0%`, `+50%`. */
export function formatColumnPct(p) {
  if (p === 0) return '0%';
  return p > 0 ? `+${p}%` : `${p}%`;
}
